/**
 * Project details card — Phase 9.
 *
 * Snapshot-style read-only view of who/where/what for the estimate.
 * Lives in the workspace panel; admin and customer see the same card.
 */

import type { Estimate, EstimateSnapshot, RoomType } from "@starz/shared/api/types.gen";

interface Props {
  estimate: Estimate;
  /** Customer's stored phone — null when admin views and the customer
   * profile hasn't loaded yet. */
  phone: string | null;
  /** Optional snapshot — if present, the ZIP factor it computed against
   * is more accurate than `dimensions_rollup`. */
  snapshot: EstimateSnapshot | null;
}

const ROOM_LABELS: Record<RoomType, string> = {
  bathroom: "Bathroom",
  kitchen: "Kitchen",
  bedroom: "Bedroom",
  living_room: "Living room",
  garage: "Garage",
  whole_house: "Whole house",
  other: "Other",
};

const RENDER_QUOTA = 20;

export function ProjectDetailsCard({ estimate, phone, snapshot }: Props) {
  const room =
    estimate.room_label ?? (estimate.room_type ? ROOM_LABELS[estimate.room_type] : null);
  const zipFactor = formatZipFactor(estimate, snapshot);

  return (
    <section
      aria-label="Project details"
      className="rounded-xl border border-starz-gold/20 bg-white p-5 shadow-sm"
    >
      <h3 className="font-serif text-base font-semibold text-starz-navy">Project details</h3>
      <dl className="mt-3 divide-y divide-starz-gold/10 text-sm">
        <Row label="Address">
          {estimate.address ? <Address address={estimate.address} /> : <Empty>Not provided</Empty>}
        </Row>
        <Row label="Phone">
          {phone ? <span className="font-mono">{formatPhoneDisplay(phone)}</span> : <Empty>—</Empty>}
        </Row>
        <Row label="Room">
          {room ? <span>{room}</span> : <Empty>—</Empty>}
        </Row>
        <Row label="ZIP factor">
          <span className="font-mono">{zipFactor}</span>
        </Row>
        <Row label="Renders">
          <span className="font-mono">
            {estimate.renders_count ?? 0}/{RENDER_QUOTA}
          </span>
        </Row>
      </dl>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2 first:pt-0 last:pb-0">
      <dt className="text-[11px] font-semibold uppercase tracking-widest text-warm-gray">
        {label}
      </dt>
      <dd className="text-right text-starz-navy">{children}</dd>
    </div>
  );
}

function Address({ address }: { address: NonNullable<Estimate["address"]> }) {
  return (
    <span className="block whitespace-pre-line">
      {address.line1}
      {address.line2 ? `\n${address.line2}` : ""}
      {`\n${address.city}, ${address.state} ${address.zip}`}
    </span>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <span className="text-warm-gray">{children}</span>;
}

/** Display E.164 → `(XXX) XXX-XXXX` for US numbers; pass through anything else. */
function formatPhoneDisplay(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

function formatZipFactor(estimate: Estimate, snapshot: EstimateSnapshot | null): string {
  // Prefer the snapshot's resolved factor — that's what was actually
  // applied to pricing. Fall back to the estimate row.
  if (snapshot?.zip_factor_used !== undefined) {
    const zip = snapshot.zip_used ?? estimate.address?.zip ?? null;
    return zip
      ? `${snapshot.zip_factor_used.toFixed(3)} (${zip})`
      : `${snapshot.zip_factor_used.toFixed(3)}`;
  }
  return estimate.address?.zip ? `1.000 (default)` : "TBD";
}
